// landing/FooterSection.jsx
import React, { useState } from 'react'; // Import useState
import { Link } from 'react-router-dom';
import { FaReddit } from 'react-icons/fa';
import ShelbulaLogo from './ShelbulaLogo';
import ContactModal from './ContactModal'; // Import the new ContactModal component


/* ========================
 * handleScrollTo - Helper function for smooth scrolling
 * ========================*/
const handleScrollTo = (e, id) => {
  e.preventDefault();
  // Remove '#' from id if present
  const targetId = id.startsWith('#') ? id.substring(1) : id;
  const element = document.getElementById(targetId);
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};

/* ========================
 * FooterLink - Renders a single link item for the footer navigation
 * This component intelligently handles three types of links:
 * 1. On-page anchor links (e.g., "#features") for smooth scrolling.
 * 2. Internal app/router links (e.g., "/terms") using React Router's Link.
 * 3. Placeholder links (no 'href') for features coming soon.
 *    Now also handles custom onClick for elements like a modal trigger.
 * ========================*/
const FooterLink = ({ text, href, onClick }) => {
  // 1. Handle anchor links for smooth scrolling
  if (href && href.startsWith('#')) {
    return (
      <li>
        <a 
          href={href} 
          onClick={(e) => handleScrollTo(e, href)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          {text}
        </a>
      </li>
    );
  }

  // 2. Handle internal app links (like /terms or /privacy)
  if (href && href.startsWith('/')) {
    return (
      <li>
        <Link 
          to={href}
          className="text-gray-400 hover:text-white transition-colors"
        >
          {text}
        </Link>
      </li>
    );
  }

  // 3. Handle links that are not yet implemented (no href) or custom onClick
  if (!href && onClick) { // If no href but an onClick is provided
    return (
      <li>
        <button 
          onClick={onClick}
          className="text-gray-400 hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0 text-left" // Style as link but use button for semantic correctness
        >
          {text}
        </button>
      </li>
    );
  }

  return (
    <li>
      <span className="text-gray-500 cursor-default">{text}</span>
    </li>
  );
};


/* ========================
 * FooterSection - Displays the final CTA, footer links, and copyright
 * ========================*/
const FooterSection = ({ onSignupClick }) => {
  const [showContactModal, setShowContactModal] = useState(false); // State for modal visibility

  const handleOpenContactModal = () => setShowContactModal(true);
  const handleCloseContactModal = () => setShowContactModal(false);

  return (
    <>
      <section className="relative py-20 px-6 bg-gradient-to-b from-black to-gray-950 border-t border-white/5 z-10">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Make AI feel like a real place to work
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Move past disposable chat tabs and into a private workspace built for ongoing projects, reusable context, and plug-and-play external APIs.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onSignupClick}
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-4 rounded-xl font-medium text-lg transition-all duration-300 shadow-lg"
            >
              Start Free
            </button>
            
            <a 
              href="#features"
              onClick={(e) => handleScrollTo(e, 'features')}
              className="bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-4 rounded-xl font-medium text-lg transition-all duration-300"
            >
              Learn More
            </a>
          </div>
          
          <p className="mt-6 text-sm text-gray-400">
            3 local workspaces free. Unlimited cloud workspaces for $9/month.
          </p>
        </div>
      </section>

      <footer className="relative py-12 px-6 border-t border-white/5 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="space-y-4">
              <ShelbulaLogo />
              <p className="text-gray-400">
                A private AI workspace with memory, tools, files, and external APIs in one place.
              </p>
              <div className="flex space-x-4">
                {/* Reddit Link using react-icons */}
                <a href="https://www.reddit.com/r/Shelbula" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                  <FaReddit className="h-5 w-5" />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium mb-4">Product</h4>
              <ul className="space-y-2">
                <FooterLink text="Features" href="#features" />
                <FooterLink text="Pricing" href="#compare"/>
                <FooterLink text="FAQs" href="#faqs"/>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-4">Coming Soon</h4>
              <ul className="space-y-2">
                <FooterLink text="Autonomous Task Office" />
                <FooterLink text="Evōk UI Studio" />
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-4">Legal</h4>
              <ul className="space-y-2">
                <FooterLink text="Terms of Service" href="/terms" />
                <FooterLink text="Privacy Policy" href="/privacy" />
                <FooterLink text="Contact" onClick={handleOpenContactModal} /> {/* Updated */}
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/5 mt-10 pt-6 text-center text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Shelbula. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <ContactModal isOpen={showContactModal} onClose={handleCloseContactModal} />
    </>
  );
};

export default FooterSection;